/*
  The keyboard, for somebody typing a whole sheet in from paper.

  Written 5 September 2026 for job 10, to the approved desktop drawing
  `docs/design/count-entry/desktop.png` (deleted 12 September 2026; kept in the tag before-phase-3-cleanup), which carries the four keys along the
  bottom of the list: **Enter save and drop to the next line, Shift and Enter
  back up one, / jump to search, N note on this line.**

  **Why it matters.** Some people count a building on paper and type the sheet
  in afterwards, hundreds of lines at a sitting. Without this every line costs a
  reach for the mouse to put the cursor in the next box, so a 486 line sheet is
  486 round trips between the keyboard and the mouse. The drawing settles the
  four keys and this is the whole of the rule they follow, kept away from the
  screen so it can be tested rather than trusted.

  **It only applies while the cursor is in a quantity box.** `/` and `n` are
  ordinary letters everywhere else, and stealing them from the search box would
  make the search box unusable.
*/

export const NEXT   = 'next';
export const PREV   = 'prev';
export const SEARCH = 'search';
export const NOTE   = 'note';
export const UNDO   = 'undo';

/*
  What one key press in a quantity box means, or nothing at all.

  A key held with Ctrl, Alt or the Command key is left alone: those are the
  browser's and the operating system's, and Ctrl and Enter is not our Enter.
*/
/*
  **The arrows and Escape joined on 17 September 2026, rebrand step 5**, from
  the brand guide's 12.3: Down saves and moves to the next line, Up to the one
  before, and Escape puts back the number the box had when the cursor went into
  it. Until then Up and Down were the browser's own, adding or taking one from
  the number. Saving still happens as each digit is typed, the owner's word of
  the same day, so "saves and moves" is the move; the save has already
  happened.
*/
export function keyAction(e) {
  if (!e || e.ctrlKey || e.metaKey || e.altKey) return null;
  if (e.key === 'Enter') return e.shiftKey ? PREV : NEXT;
  if (e.key === 'ArrowDown') return NEXT;
  if (e.key === 'ArrowUp') return PREV;
  if (e.key === 'Escape') return UNDO;
  if (e.key === '/') return SEARCH;
  if (e.key === 'n' || e.key === 'N') return NOTE;
  return null;
}

/*
  Which line the cursor lands on next, or -1 for nowhere.

  **The ends of the sheet stop rather than wrap.** Enter on the last line of 486
  wrapping back to the first would put somebody typing at speed on line 1 with
  no idea they had moved, and the next number they type overwrites a line they
  counted an hour ago.
*/
export function nextIndex(index, action, length) {
  const step = action === NEXT ? 1 : action === PREV ? -1 : 0;
  if (step === 0) return -1;
  const to = index + step;
  if (to < 0 || to >= length) return -1;
  return to;
}

/*
  A key that would put something other than a digit in a count box (guide 12.3:
  "letters are rejected with a jostle and red outline, and the cursor stays
  put"). A number box in a browser takes `e`, `.`, `+` and `-` as well, and
  none of those is a count. Keys that do something (Enter, the arrows, Tab,
  Backspace) are more than one character long and are left alone, and so is
  anything held with Ctrl, Alt or Command.
*/
export function refusesKey(e) {
  if (!e || e.ctrlKey || e.metaKey || e.altKey) return false;
  if (typeof e.key !== 'string' || e.key.length !== 1) return false;
  return !/[0-9]/.test(e.key);
}
